import { ComponentTypes } from "entities/template/lib/const/componentTypes";
import type {
    IAutonumberCounter,
    ITemplateAutonumberComponent,
    ITemplateComponent,
    ITemplatesService
} from "entities/template/lib/types/template_service_types";
import { autonumberCounterToComponent, DEFAULT_AUTONUMBER_MASK } from "./autonumber";

type AutonumberReadService = Pick<ITemplatesService, "loadAutonumberCounters">;

const asAutonumberDraft = (component: ITemplateComponent): ITemplateAutonumberComponent => ({
    ...component,
    counterName: (component as ITemplateAutonumberComponent).counterName || "",
    name: component.name || "",
    mask: (component as ITemplateAutonumberComponent).mask || DEFAULT_AUTONUMBER_MASK,
    isAttrUsed: (component as ITemplateAutonumberComponent).isAttrUsed || false,
    counterReadOnly: false
} as ITemplateAutonumberComponent);

export const hydrateAutonumberComponents = async (
    components: ITemplateComponent[],
    login: string,
    service: AutonumberReadService
): Promise<ITemplateComponent[]> => {
    const countersById = new Map<number, Promise<IAutonumberCounter | undefined>>();

    components.forEach(item => {
        if (item.type !== ComponentTypes.autonumber) {
            return;
        }

        const autonumberId = (item as ITemplateAutonumberComponent).autonumberId;
        if (autonumberId !== undefined && !countersById.has(autonumberId)) {
            countersById.set(
                autonumberId,
                service.loadAutonumberCounters({ id: String(autonumberId) }).then(counters => counters[0])
            );
        }
    });

    return Promise.all(components.map(async item => {
        if (item.type !== ComponentTypes.autonumber) {
            return item;
        }

        const component = asAutonumberDraft(item);
        if (component.autonumberId === undefined) {
            return component;
        }

        try {
            const counter = await countersById.get(component.autonumberId);
            if (!counter) {
                return { ...component, counterLoadError: "not-found" };
            }
            return {
                ...autonumberCounterToComponent(counter, component, components),
                counterReadOnly: counter.login !== login
            };
        } catch (error) {
            const status = (error as { status?: number }).status;
            return {
                ...component,
                counterLoadError: status === 404 ? "not-found" : "load-failed"
            };
        }
    }));
};

export const filterOwnAutonumberCounters = (
    counters: IAutonumberCounter[],
    login: string
): IAutonumberCounter[] => counters.filter(counter => counter.login === login);
